import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CalibrationApi from '../../../api/CalibrationApi'
import {
  Coordinate,
  CartesianCoordinate,
  CylindricalCoordinate,
} from '../../../Model/Coordinate'
import { EModuleType } from '../../../Model/EModuleType'
import { Waypoint, EArmType, EAxisType } from '../../../Model/Waypoint'
import { getModuleById } from '../../../redux/reducers/ModuleReducer'
import { getModuleState } from '../../../redux/reducers/ModuleStateReducer'
import { useTypedSelector } from '../../../redux/reducers/RootReducer'
import MtiButton from '../../Buttons/MtiButton'
import SwitchButton from '../../Buttons/SwitchButton'
import CartesianArmMover from '../../Common/CartesianArmMover'
import WaypointOffset from './WaypointOffset'
import DefaultVelocity from './DefaultVelocity'
import './SettingTools.css'
import VectorEditorComponent from './VectorEditorComponent'
import { ECarrier } from '../../../Model/ECarrier'
import AutoCalibrationUtil from '../../../Utility/AutoCalibrationUtil'
import CylindricalArmMover from '../../Common/CylindricalArmMover'
import { getSuiteConfiguration } from '../../../redux/reducers/InitialConfigurationReducer'
import { EInstrumentType, EPsrPosition, EPsrState, ESuiteMode, ESuiteModeState } from '../../../Model/ModuleState'
import CommonDropDown from '../../Common/DropDown/CommonDropDown'
import FindSafePositionModalProgress from './FindSafePositionModalProgress'
import FindSafePositionSuccessModal from './FindSafePositionSuccessModal'
import FindSafePositionFailedModal from './FindSafePositionFailedModal'

type Prop = {
  moduleIdentifier: string
  armType: EArmType
  waypoint: Waypoint
  onWaypointUpdated: (waypoint: Waypoint) => void
  applyOffsetWhileTuning: boolean
  setApplyOffsetWhileTuning: (applyOffsetWhileTuning: boolean) => void
  selectedCarrier: ECarrier
  onSelectedCarrier: (selectedCarrier: ECarrier) => void
}

type CarrierDisplayValue = {
  display: string
  value: ECarrier
  disabled: boolean
}

const carriersSelection: CarrierDisplayValue[] = [
  ECarrier.Imager,
  ECarrier.LoadPort,
  ECarrier.FixedCarriage,
  ECarrier.MovingCarriage1,
  ECarrier.MovingCarriage2,
].map((carrier) => ({
  display: AutoCalibrationUtil.getCarrierName(carrier),
  disabled: false,
  value: carrier,
}))

type PopupModalState = "IDLE" | "INPROGRESS" | "SUCCESS" | "FAIL";

const getInitialModalState = (
  suiteMode: ESuiteMode,
  psrState: EPsrState,
  findingSafePositionState: ESuiteModeState
): PopupModalState => {
  if (
    suiteMode === ESuiteMode.FindingSafePosition &&
    psrState === EPsrState.Busy &&
    (findingSafePositionState === ESuiteModeState.Started ||
      findingSafePositionState === ESuiteModeState.Stopping)
  ) {
    return "INPROGRESS";
  } else {
    return "IDLE";
  }
};

const getUserIntentionallyStopLocalStorage = (
  autoCalibratingState: ESuiteModeState
) => {
  if (autoCalibratingState === ESuiteModeState.Stopping) {
    const resultString = localStorage.getItem("USER_STOP_FINDING_SAFE_POSITION");
    return resultString === "true" ? true : false;
  }

  return false;
};

const setUserIntentionallyStopLocalStorage = (value: boolean) => {
  localStorage.setItem("USER_STOP_FINDING_SAFE_POSITION", value.toString());
};


export default function SettingTools({
  moduleIdentifier,
  armType,
  waypoint,
  onWaypointUpdated,
  applyOffsetWhileTuning,
  setApplyOffsetWhileTuning,
  selectedCarrier,
  onSelectedCarrier,
}: Prop) {
  const motionControlRef = useRef<HTMLDivElement | null>(null);
  const suitConfig = useTypedSelector((state) => getSuiteConfiguration(state))
  const moduleState = useTypedSelector((state) =>
    getModuleState(state, moduleIdentifier),
  )
  const suiteState = useTypedSelector(
    (state) => state.moduleStateStore.moduleStateById["Suite"]?.suite
  );
  const findingSafePositionState = suiteState?.findingXSafePosition
  const imagerState = moduleState.imager
  const psrState = moduleState.psr
  
  const isPlateBumpActivated = imagerState?.isPlateBumpActivated
  const isElectromagnetEngaged = psrState?.isElectromagnetEngaged
  const isPickUpMotorExtended = psrState?.isPickUpMotorExtended
  const isAllowMoveToSafePosition = psrState != null;
  const suiteMode = suiteState?.suiteMode ?? ESuiteMode.FindingSafePosition;


  const isMoveToDisabled = useMemo(() => {
    switch (selectedCarrier) {
      case ECarrier.Imager:
      case ECarrier.LoadPort:
      case ECarrier.MovingCarriage1:
        return psrState?.xSafePositionCoordinate.frontOfMovableCarrier1 ? false : true
      case ECarrier.FixedCarriage:
        return psrState?.xSafePositionCoordinate.frontOfFixedCarriage ? false : true
      case ECarrier.MovingCarriage2:
        return psrState?.xSafePositionCoordinate.frontOfMovableCarrier2 ? false : true
      default:
        return false
    }
  },[selectedCarrier, psrState])

  const [popupModalState, setPopupModalState] = useState(
    getInitialModalState(
      suiteMode,
      psrState?.state ?? EPsrState.Unknown,
      findingSafePositionState?.modeState ?? ESuiteModeState.Starting
    )
  );

  const [userIntentionallyStop, setUserIntentionallyStop] = useState(
    getUserIntentionallyStopLocalStorage(
      findingSafePositionState?.modeState ?? ESuiteModeState.Starting
    )
  );

  const needShowModalAfterProgress = useRef(popupModalState === "INPROGRESS");

  const onPreventDefaltArrowKey = useCallback((e: KeyboardEvent) => {
    if (["ArrowUp", "ArrowDown"].indexOf(e.code) > -1) {
      e.preventDefault();
    }
  }, []);

  const handleOutsideRefClick = useCallback((e: any ) => {
    if (!motionControlRef.current?.contains(e.target)) {
      document.removeEventListener("keydown", onPreventDefaltArrowKey)
    }
  },[onPreventDefaltArrowKey])

  const handleRefClick = useCallback(() => {
    document.addEventListener("keydown", onPreventDefaltArrowKey)
  }, [onPreventDefaltArrowKey]);


  const setForwardOffset = (
    forwardOffset: CartesianCoordinate | CylindricalCoordinate,
  ) => {
    onWaypointUpdated({
      ...waypoint,
      forwardOffset: forwardOffset,
    })
  }

  const setReverseOffset = (
    reverseOffset: CartesianCoordinate | CylindricalCoordinate,
  ) => {
    onWaypointUpdated({
      ...waypoint,
      reverseOffset: reverseOffset,
    })
  }

  const setElectromagnet = (isActivated: boolean) => {
    CalibrationApi.setElectromagnet(moduleIdentifier, isActivated)
  }

  const setPlateBump = (isActivated: boolean) => {
    CalibrationApi.setPlateBump(moduleIdentifier, isActivated)
  }

  function convertToPsrPosition(selectedCarrier: ECarrier) {
    switch (selectedCarrier) {
      case ECarrier.Imager:
        return EPsrPosition.FrontOfImager
      case ECarrier.LoadPort:
        return EPsrPosition.FrontOfLoadPort
      case ECarrier.FixedCarriage:
        return EPsrPosition.FrontOfFixedCarriage
      case ECarrier.MovingCarriage1:
        return EPsrPosition.FrontOfMovableCarrier1
      case ECarrier.MovingCarriage2:
        return EPsrPosition.FrontOfMovableCarrier2
      default:
        return EPsrPosition.Unknown
    }
  }

  const onStopFindingSafePosition = () => {
    setUserIntentionallyStop(true);
    setUserIntentionallyStopLocalStorage(true);
    CalibrationApi.stopFindSafePosition(moduleIdentifier)
  };

  const onClickCloseDone = useCallback(() => {
    setPopupModalState("IDLE");
    needShowModalAfterProgress.current = false;
  }, []);

  useEffect(() => {
    if (suiteMode === ESuiteMode.FindingSafePosition) {
      //show in progress only when psr is busy
      if ((psrState ? psrState.state : EPsrState.Unknown) === EPsrState.Busy) {
        setPopupModalState("INPROGRESS");
        needShowModalAfterProgress.current = true;
      }
    } else if (needShowModalAfterProgress.current) {
      if (psrState?.state === EPsrState.Fail) {
        if (!userIntentionallyStop) {
          setPopupModalState("FAIL");
        } else {
          setPopupModalState("IDLE");
          needShowModalAfterProgress.current = false;
        }
      } else {
        setPopupModalState("SUCCESS");
      }
    }

    document.addEventListener("mousedown", handleOutsideRefClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideRefClick);
    }
  }, [suiteMode, findingSafePositionState, handleOutsideRefClick, psrState, userIntentionallyStop]);
  return (
    <div className="setting-tools-container">
      <VectorEditorComponent onClickRef={handleRefClick} ref={motionControlRef} style={{ width: '100%' }}>
        {waypoint.axisType === EAxisType.Cartesian ? (
          <CartesianArmMover
            moduleIdentifier={moduleIdentifier}
            armType={armType}
          />
        ) : (
          <CylindricalArmMover
            moduleIdentifier={moduleIdentifier}
            armType={armType}
          />
        )}
      </VectorEditorComponent>
      {suitConfig.instrumentType === EInstrumentType.RI360
        ? AutoCalibrationUtil.ExpandableCarrier.map((x) => x.value).includes(
            selectedCarrier,
          ) && (
            <VectorEditorComponent style={{ width: '100%' }}>
              {isAllowMoveToSafePosition&& (
                <div className="safe-position-wrapper">
                  <MtiButton
                    colorVariant="alternate"
                    variant="secondary"
                    size="medium"
                    onClick={() => {
                      CalibrationApi.runFindSafePosition(moduleIdentifier)
                    }}
                    style={{
                      minWidth: 45,
                      height: 40,
                      margin: '10px 10px 0px 10px',
                    }}
                  >
                    GET SAFE POSITION
                  </MtiButton>
                  <div className="safe-position">
                    <div>FC:</div>
                    <div className="safe-position-value">
                      {typeof psrState?.xSafePositionCoordinate?.frontOfFixedCarriage === 'number'
                        ? psrState.xSafePositionCoordinate?.frontOfFixedCarriage.toFixed(
                            2,
                          )
                        : '-'}
                    </div>
                  </div>
                  <div className="safe-position">
                    <div>MC1:</div>
                    <div className="safe-position-value">
                      {typeof psrState?.xSafePositionCoordinate?.frontOfMovableCarrier1 === 'number'
                        ? psrState.xSafePositionCoordinate?.frontOfMovableCarrier1.toFixed(
                            2,
                          )
                        : '-'}
                    </div>
                  </div>
                  <div className="safe-position">
                    <div>MC2:</div>
                    <div className="safe-position-value">
                      {typeof psrState?.xSafePositionCoordinate?.frontOfMovableCarrier2 === 'number'
                        ? psrState.xSafePositionCoordinate?.frontOfMovableCarrier2.toFixed(
                            2,
                          )
                        : '-'}
                    </div>
                  </div>
                </div>
              )}
              <div
                data-testid="open-carrier"
                className="setting-tools-action-button"
              >
                {isAllowMoveToSafePosition ? (
                 
                  <CommonDropDown<number>
                    value={selectedCarrier}
                    id="carrier-select"
                    onChange={(value) => {
                      onSelectedCarrier(value as ECarrier)
                    }}
                    style={{
                      width: 250,
                      minHeight: 32,
                      borderRadius: 4,
                    }}
                    dropDownDisplayValues={carriersSelection}
                  />
                ) : (
                  <span>
                  {AutoCalibrationUtil.ExpandableCarrier.filter(
                    (x) => x.value === selectedCarrier,
                  )[0].display + " is available to be opened"}{" "}
                </span>
                )}
                <MtiButton
                  onClick={() =>
                    CalibrationApi.ExpandCarrier(
                      moduleIdentifier,
                      selectedCarrier,
                    )
                  }
                  style={{
                    alignSelf: 'flex-end',
                    letterSpacing: 1.25,
                    minHeight: 36,
                    width: 106,
                    fontSize: 16,
                    marginLeft: 16,
                  }}
                >
                  <span>OPEN</span>
                </MtiButton>
                {isAllowMoveToSafePosition && (
                  <MtiButton
                    onClick={() => {
                      var psrPosition = convertToPsrPosition(selectedCarrier)
                      CalibrationApi.moveToSafePosition(moduleIdentifier,psrPosition)
                    }}
                    style={{
                      alignSelf: 'flex-end',
                      letterSpacing: 1.25,
                      minHeight: 36,
                      width: 106,
                      fontSize: 16,
                      marginLeft: 16,
                    }}
                    disabled={isMoveToDisabled}
                  >
                    <span>MOVE TO</span>
                  </MtiButton>
                )}
              </div>
            </VectorEditorComponent>
          )
        : null}
      {(isPickUpMotorExtended != null ||
        isElectromagnetEngaged != null ||
        isPlateBumpActivated != null) && (
        <VectorEditorComponent style={{ width: '100%' }}>
          <div>
            {isElectromagnetEngaged != null && (
              <div
                data-testid="electromagnet-controller"
                className="setting-tools-action-button"
              >
                <span>Electromagnet Controller</span>
                <SwitchButton
                  withLetter={true}
                  isOn={isElectromagnetEngaged}
                  onToggle={() => setElectromagnet(!isElectromagnetEngaged)}
                />
              </div>
            )}
            {isPlateBumpActivated != null && (
              <div
                data-testid="plate-bump-switch"
                className="setting-tools-action-button"
              >
                <span>Plate Bump</span>
                <SwitchButton
                  withLetter={true}
                  isOn={isPlateBumpActivated}
                  onToggle={() => setPlateBump(!isPlateBumpActivated)}
                />
              </div>
            )}
            {isPickUpMotorExtended != null && (
              <div
                data-testid="pickup-motor-controller"
                style={{ display: 'flex' }}
              >
                <MtiButton
                  colorVariant="alternate"
                  variant="secondary"
                  size="medium"
                  onClick={() =>
                    CalibrationApi.setPickupMotor(moduleIdentifier, true)
                  }
                  disabled={isPickUpMotorExtended}
                  style={{
                    minWidth: 40,
                    height: 32,
                    margin: '10px 10px 0px 10px',
                  }}
                >
                  Extend
                </MtiButton>
                <MtiButton
                  colorVariant="alternate"
                  variant="secondary"
                  size="medium"
                  onClick={() =>
                    CalibrationApi.setPickupMotor(moduleIdentifier, false)
                  }
                  disabled={!isPickUpMotorExtended}
                  style={{ minWidth: 40, height: 32, marginTop: '10px' }}
                >
                  Retract
                </MtiButton>
              </div>
            )}
          </div>
        </VectorEditorComponent>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <VectorEditorComponent>
          <WaypointOffset
            forwardOffset={waypoint.forwardOffset}
            reverseOffset={waypoint.reverseOffset}
            setForwardOffsetHandler={setForwardOffset}
            setReverseOffsetHandler={setReverseOffset}
            isApplyOffsetWhileTuning={applyOffsetWhileTuning}
            setIsApplyOffsetWhileTuning={setApplyOffsetWhileTuning}
          />
        </VectorEditorComponent>
        <VectorEditorComponent>
          <DefaultVelocity velocityOnAxes={waypoint.defaultVelocity} />
        </VectorEditorComponent>
        <FindSafePositionModalProgress 
           show={popupModalState === "INPROGRESS"}
           onHide={() => {}}
           findSafePositionState={findingSafePositionState?.modeState ?? ESuiteModeState.Starting}
           stopFinding={onStopFindingSafePosition}
        />
        <FindSafePositionSuccessModal 
            show={popupModalState === "SUCCESS"}
            onHide={onClickCloseDone}
        />
        <FindSafePositionFailedModal
        show={popupModalState === "FAIL"}
        onHide={onClickCloseDone}
        disableAnimation={true}
      />
      </div>
    </div>
  )
}
